/**
 * Pure maths for rendering a drawing's PDF page into a canvas that sits inside
 * the zoomed/panned drawn box of BlueprintWithMarkers (plan §5.1).
 *
 * Coordinate spaces:
 * - box CSS px: the unzoomed drawn box (width = container width, height from the
 *   node's aspect). Markers and the canvas element are positioned in this space;
 *   the CSS transform `translate(pan) scale(zoom)` is applied on top.
 * - device px: box CSS px · zoom · dpr. This is the canvas backing store.
 */

/** Canvas backing-store budget in pixels (≈ 16 Mpx; 64 MB of RGBA). */
export const MAX_CANVAS_PIXELS = 16_000_000;

/** Extra visible area rendered around the viewport in region mode, per side, as a fraction of the container. */
export const REGION_OVERSCAN = 0.5;

export interface RenderInput {
    /** Drawn box size in CSS px (unzoomed). */
    boxWidth: number;
    boxHeight: number;
    /** Current zoom and pan of the box's CSS transform. */
    zoom: number;
    pan: { x: number; y: number };
    /** The scroll/clip container in CSS px. */
    containerWidth: number;
    containerHeight: number;
    /** window.devicePixelRatio */
    dpr: number;
}

export interface RenderPlan {
    /** 'full' draws the whole page once per zoom bucket; 'region' draws only what is visible. */
    mode: 'full' | 'region';
    /** Canvas element position and size in box CSS px. */
    left: number;
    top: number;
    width: number;
    height: number;
    /** Canvas backing store in device px. */
    canvasWidth: number;
    canvasHeight: number;
    /** Device px per box CSS px for this render (zoom · dpr, reduced when the budget caps a full render). */
    pixelsPerBoxPx: number;
    /** The zoom bucket a full render belongs to (region renders use the exact zoom). */
    bucket: number;
}

/**
 * Zoom buckets are powers of two, so zooming in a little does not throw away a
 * whole-page render that is still sharp enough.
 */
export function zoomBucket(zoom: number): number {
    if (!(zoom > 0)) return 1;
    return Math.pow(2, Math.ceil(Math.log2(zoom)));
}

/**
 * Decide what to draw for the current view. A whole-page render at the current
 * zoom bucket is preferred while it fits the pixel budget (it survives panning and
 * small zoom steps); past that, only the visible region plus overscan is rendered.
 */
export function planDraw(input: RenderInput): RenderPlan | null {
    const { boxWidth, boxHeight, zoom, pan, containerWidth, containerHeight } = input;
    const dpr = input.dpr > 0 ? input.dpr : 1;
    if (!(boxWidth > 0) || !(boxHeight > 0) || !(zoom > 0)) return null;

    const bucket = zoomBucket(zoom);
    const fullPixels = boxWidth * bucket * dpr * boxHeight * bucket * dpr;
    if (fullPixels <= MAX_CANVAS_PIXELS) {
        const ppp = bucket * dpr;
        return {
            mode: 'full',
            left: 0,
            top: 0,
            width: boxWidth,
            height: boxHeight,
            canvasWidth: Math.round(boxWidth * ppp),
            canvasHeight: Math.round(boxHeight * ppp),
            pixelsPerBoxPx: ppp,
            bucket,
        };
    }

    // The fit render (zoom 1) may itself exceed the budget on a huge box; scale it down.
    if (!(containerWidth > 0) || !(containerHeight > 0)) {
        const ppp = Math.min(dpr, Math.sqrt(MAX_CANVAS_PIXELS / (boxWidth * boxHeight)));
        return {
            mode: 'full',
            left: 0,
            top: 0,
            width: boxWidth,
            height: boxHeight,
            canvasWidth: Math.round(boxWidth * ppp),
            canvasHeight: Math.round(boxHeight * ppp),
            pixelsPerBoxPx: ppp,
            bucket,
        };
    }

    // Visible part of the box, in box CSS px.
    const viewLeft = -pan.x / zoom;
    const viewTop = -pan.y / zoom;
    const viewWidth = containerWidth / zoom;
    const viewHeight = containerHeight / zoom;

    // Overscan, shrunk if needed so the region canvas stays within budget.
    let overscan = REGION_OVERSCAN;
    const containerPixels = containerWidth * dpr * containerHeight * dpr;
    const maxGrowth = Math.sqrt(MAX_CANVAS_PIXELS / containerPixels); // (1 + 2·overscan) may not exceed this
    if (1 + 2 * overscan > maxGrowth) {
        overscan = Math.max(0, (maxGrowth - 1) / 2);
    }

    const left = Math.max(0, viewLeft - viewWidth * overscan);
    const top = Math.max(0, viewTop - viewHeight * overscan);
    const right = Math.min(boxWidth, viewLeft + viewWidth * (1 + overscan));
    const bottom = Math.min(boxHeight, viewTop + viewHeight * (1 + overscan));
    if (right <= left || bottom <= top) return null; // nothing of the drawing is visible

    const ppp = zoom * dpr;
    return {
        mode: 'region',
        left,
        top,
        width: right - left,
        height: bottom - top,
        canvasWidth: Math.max(1, Math.round((right - left) * ppp)),
        canvasHeight: Math.max(1, Math.round((bottom - top) * ppp)),
        pixelsPerBoxPx: ppp,
        bucket,
    };
}

/**
 * True when what is already on screen (`rendered`) still serves the new plan:
 * same mode and sharpness, and for a region render the wanted area lies inside
 * the drawn one. Pans inside the overscan and zoom steps inside a bucket then
 * cost nothing.
 */
export function planIsCovered(rendered: RenderPlan | null, wanted: RenderPlan): boolean {
    if (!rendered) return false;
    if (rendered.mode !== wanted.mode) return false;
    if (rendered.width <= 0 || wanted.width <= 0) return false;
    if (rendered.mode === 'full') {
        return rendered.bucket === wanted.bucket
            && rendered.canvasWidth === wanted.canvasWidth
            && rendered.canvasHeight === wanted.canvasHeight;
    }
    if (Math.abs(rendered.pixelsPerBoxPx - wanted.pixelsPerBoxPx) > 1e-6) return false;
    const eps = 0.5;
    return wanted.left >= rendered.left - eps
        && wanted.top >= rendered.top - eps
        && wanted.left + wanted.width <= rendered.left + rendered.width + eps
        && wanted.top + wanted.height <= rendered.top + rendered.height + eps;
}

/**
 * pdf.js viewport parameters for a plan: `scale` maps page points to device px so
 * the whole page spans the box; `offsetX/offsetY` shift the page so the plan's
 * region lands at the canvas origin. `boxWidth` is the full drawn box width
 * (a region plan only carries its own rectangle).
 */
export function viewportParamsFor(plan: RenderPlan, boxWidth: number, pageWidthPt: number): { scale: number; offsetX: number; offsetY: number } {
    if (!(pageWidthPt > 0) || !(boxWidth > 0)) return { scale: 1, offsetX: 0, offsetY: 0 };
    return {
        scale: (boxWidth * plan.pixelsPerBoxPx) / pageWidthPt,
        offsetX: -plan.left * plan.pixelsPerBoxPx,
        offsetY: -plan.top * plan.pixelsPerBoxPx,
    };
}
